import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import {
  fetchAdminMetrics,
  fetchCanonical,
  fetchSubjects,
  fetchTranscript,
} from "../lib/api";
import OperatorControls from "../components/OperatorControls";
import StallAlerts from "../components/StallAlerts";
import LiveWatch from "../components/LiveWatch";
import EngagementCompare from "../components/EngagementCompare";
import ProbeBank from "../components/ProbeBank";
import Avoidance from "../components/Avoidance";
import Benchmark from "../components/Benchmark";
import Operators from "../components/Operators";
import Progress from "../components/Progress";
import Invites from "../components/Invites";
import OperatorNav, { MONITOR_VIEWS } from "../components/operator/OperatorNav";
import CommandPalette from "../components/operator/CommandPalette";
import ConsoleHome from "../components/operator/ConsoleHome";
import SubjectPicker from "../components/operator/SubjectPicker";
import VitalsBar from "../components/operator/VitalsBar";
import ConvergencePanel from "../components/operator/ConvergencePanel";
import RhythmPanel from "../components/operator/RhythmPanel";
import TimelinePanel from "../components/operator/TimelinePanel";
import CohortPanel from "../components/operator/CohortPanel";
import EdgesPanel from "../components/operator/EdgesPanel";
import ControlItemsPanel from "../components/operator/ControlItemsPanel";
import TranscriptPanel from "../components/operator/TranscriptPanel";
import SpecPanel from "../components/operator/SpecPanel";
import FacetsPanel from "../components/operator/FacetsPanel";
import FacetTrendsPanel from "../components/operator/FacetTrendsPanel";

export default function Operator() {
  const [subjects, setSubjects] = useState([]);
  const [subject, setSubject] = useState(null);
  const [m, setM] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [tx, setTx] = useState(null);
  const [canon, setCanon] = useState(null);

  useEffect(() => {
    fetchSubjects().then((d) => setSubjects(d.subjects)).catch(() => {});
  }, []);

  const load = useCallback(
    () => fetchAdminMetrics(subject).then(setM).catch(() => {}),
    [subject]
  );

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    setTx(null);
  }, [subject]);

  useEffect(() => {
    if (tab === "Transcript" && !tx) fetchTranscript(subject).then(setTx).catch(() => {});
    if (tab === "Spec" && !canon) fetchCanonical().then(setCanon).catch(() => {});
  }, [tab, subject]); // eslint-disable-line react-hooks/exhaustive-deps

  const who = subject || m?.subject;
  const monitoring = MONITOR_VIEWS.includes(tab);

  const watch = (s) => {
    setSubject(s);
    setTab("Live Watch");
  };

  const panels = {
    Overview: <ConsoleHome onWatch={watch} />,
    Control: <OperatorControls subject={who} timer={m?.timer} onChanged={load} />,
    "Live Watch": <LiveWatch subject={who} />,
    Progress: <Progress subject={who} />,
    Convergence: <ConvergencePanel m={m} />,
    Instruments: <ProbeBank subject={who} />,
    "Control items": <ControlItemsPanel m={m} />,
    Facets: <FacetsPanel m={m} />,
    Avoidance: <Avoidance subject={who} />,
    Cohort: <CohortPanel m={m} />,
    Influence: <EdgesPanel m={m} />,
    Benchmark: <Benchmark subject={who} />,
    Engagements: <EngagementCompare subjects={subjects} />,
    "Facet trends": <FacetTrendsPanel />,
    Rhythm: <RhythmPanel m={m} subject={who} />,
    Timeline: <TimelinePanel m={m} />,
    Transcript: (
      <TranscriptPanel tx={tx} onRefresh={() => fetchTranscript(subject).then(setTx)} />
    ),
    Invites: <Invites />,
    Operators: <Operators />,
    Spec: <SpecPanel canon={canon} />,
  };

  return (
    <div
      data-testid="admin-panel-container"
      className="grain relative flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--void)] lg:flex-row"
    >
      <OperatorNav tab={tab} setTab={setTab} />
      <CommandPalette subjects={subjects} onView={setTab} onSubject={watch} />

      <div className="thin-scroll relative min-w-0 flex-1 overflow-y-auto">
        <header className="sticky top-0 z-30 border-b border-[var(--line)] bg-[rgba(5,5,5,0.86)] px-6 py-5 backdrop-blur-md sm:px-10">
          <div className="flex flex-wrap items-end justify-between gap-5">
            <div className="min-w-0">
              <p className="label">Operator console · not visible to subjects</p>
              <h1 className="mt-2 font-display text-3xl leading-none tracking-tight">{tab}</h1>
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <span className="label hidden !text-[9px] sm:inline">⌘K · alt+K to jump</span>
              <button
                onClick={load}
                data-testid="operator-refresh-btn"
                className="label flex items-center gap-1.5 hover:text-zinc-200"
              >
                <RefreshCw size={11} strokeWidth={1.6} /> Refresh
              </button>
              <SubjectPicker subjects={subjects} subject={subject} onPick={setSubject} />
            </div>
          </div>

          <p className="mt-4 font-data text-[10px] text-zinc-700">
            {m?.spec_version} · {m?.model} · session {m?.session_id?.slice(0, 8)}
          </p>
        </header>

        <div className="px-6 py-8 sm:px-10">
          {monitoring && (
            <div className="mb-10 space-y-4">
              <VitalsBar m={m} />
              <StallAlerts onWatch={watch} />
            </div>
          )}

          {panels[tab]}
        </div>
      </div>
    </div>
  );
}
